import { middleware } from '@seek/metrics-js';
import { v4 as createGuid } from 'uuid';

import type { AppConfig } from 'src/modules/appConfig/types';
import type { ApiClient } from 'src/types';

import { middlewareErrorLogger } from '../modules/error-logger';

import { createCareerHistoryAnalyticsMiddleware } from './analytics/careerHistory/createCareerHistoryAnalyticsMiddleware';
import { createCertsyAnalyticsMiddleware } from './analytics/certsy/createCertsyAnalyticsMiddleware';
import { createEducationAnalyticsMiddleware } from './analytics/education/createEducationAnalyticsMiddleware';
import { createLanguagesAnalyticsMiddleware } from './analytics/languages/createLanguagesAnalyticsMiddleware';
import { createLicencesAnalyticsMiddleware } from './analytics/licences/createLicencesAnalyticsMiddleware';
import { createNextRoleAnalyticsMiddleware } from './analytics/nextRole/createNextRoleAnalyticsMiddleware';
import { createPersonalSummaryAnalyticsMiddleware } from './analytics/personalSummary/createPersonalSummaryAnalyticsMiddleware';
import { createResumeAnalyticsMiddleware } from './analytics/resume/createResumeAnalyticsMiddleware';
import { createSkillsAnalyticsMiddleware } from './analytics/skills/createSkillsAnalyticsMiddleware';
import {
  createAnalyticsMiddleware,
  type Analytics,
} from './createAnalyticsMiddleware';
import { createBlendMiddleware } from './createBlendMiddleware';
import { createHotjarMiddleware } from './createHotjarMiddleware';
import { createMetricsMiddleware } from './createMetricsMiddleware';
import { createSyncingMiddleware } from './createSyncingMiddleware';
import { createDependencyInjector } from './dependencyInjector';

export const createMiddlewareList = ({
  analytics,
  apiClient,
  appConfig,
}: {
  analytics: Analytics;
  apiClient: ApiClient;
  appConfig: AppConfig;
}) => {
  const analyticsMiddlewares = [
    createAnalyticsMiddleware(analytics, apiClient, appConfig),
    createSkillsAnalyticsMiddleware(analytics, apiClient, appConfig),
    createNextRoleAnalyticsMiddleware(analytics, apiClient, appConfig),
    createEducationAnalyticsMiddleware(analytics, apiClient, appConfig),
    createLanguagesAnalyticsMiddleware(analytics),
    createLicencesAnalyticsMiddleware(analytics),
    createCareerHistoryAnalyticsMiddleware(analytics, apiClient),
    createPersonalSummaryAnalyticsMiddleware(analytics, apiClient),
    createResumeAnalyticsMiddleware(analytics, apiClient),
    createCertsyAnalyticsMiddleware(analytics),
  ];

  return [
    createDependencyInjector({ api: apiClient, createGuid, appConfig }),
    createBlendMiddleware({ api: apiClient, appConfig }),
    middlewareErrorLogger(),
    ...analyticsMiddlewares,
    middleware.reduxActionMetrics({}),
    createSyncingMiddleware(),
    createMetricsMiddleware(apiClient, appConfig),
    createHotjarMiddleware(),
  ];
};
